import React, { useEffect, useMemo, useState } from 'react';

import moment from 'moment';

import { useStore } from '../../../stores/root';

import styles from './styles.module.scss';

import { 

    IOperationItem, 

    TOperationActivityType, 

    TOperationStatusFilter, 

    TOperationTypeFilter, 

    TOperationViewMode 

} from './types';

import WorkspaceHeader from './header';

import WorkspaceTableView from './views/table';

import WorkspaceBoardView from './views/board';

import RescheduleModal from './modals/reschedule';

import QuickStatusModal from './modals/quickStatus';
import OrderTrackingStatusForm from '../../orders/trackings/general/status';
import ShipScheduleStatusForm from '../ships/schedules/general/status';
import TrainScheduleStatusForm from '../trains/schedules/general/status';
import ActiveOrdersModal from './modals/activeOrdersModal';
import { IActiveOrdersRecap, IActiveOrderInfo } from './types';

import ActivitiesService from '../../../services/activities/activities';

import { IActivityResourceShortProps } from '../../../props/activities';

import LoadingComponent from '../../feedbacks/loading';

import ErrorService from '../../../services/general/error';

import { faAnchor, faBox, faBoxesPacking, faCheck, faDolly, faShip, faTag, faTruck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

import OrderService from '../../../services/orders/order';



const OperationsWorkspace: React.FC = () => {

    const { banner } = useStore();

    const [loaded, setLoaded] = useState<boolean>(false);

    const [activities, setActivities] = useState<IActivityResourceShortProps | undefined>();



    // Filter & Navigation States

    const [selectedDate, setSelectedDate] = useState<{ startDate: string; endDate: string }>({

        startDate: moment().startOf('day').toISOString(),

        endDate: moment().endOf('day').toISOString(),

    });

    const [statusFilter, setStatusFilter] = useState<TOperationStatusFilter>('all');

    const [typeFilter, setTypeFilter] = useState<TOperationTypeFilter>('all');

    const [searchKeyword, setSearchKeyword] = useState<string>('');

    const [viewMode, setViewMode] = useState<TOperationViewMode>('table');



    // Modal States

    const [activeRescheduleItem, setActiveRescheduleItem] = useState<IOperationItem | undefined>();

    const [activeStatusItem, setActiveStatusItem] = useState<IOperationItem | undefined>();
    const [isOrdersModalOpen, setIsOrdersModalOpen] = useState<boolean>(false);



    useEffect(() => {

        loadActivities();

    }, [selectedDate]);



    const loadActivities = async () => {

        try {

            setLoaded(false);

            const qs = `startDate=${selectedDate.startDate}&endDate=${selectedDate.endDate}`;

            const res = await ActivitiesService.retrieve(qs);

            setActivities(res);

            setLoaded(true);

        } catch (e) {

            banner.add({

                key: 'load_operations_error',

                variant: 'error',

                icon: faXmarkCircle,

                text: 'Failed to load operational activities. Error: ' + ErrorService.getMessage(e),

            });

            setLoaded(true);

        }

    };



    // Normalize raw backend activities into unified IOperationItem[]

    // Tahap 3 Guardrail: Limit active operational overdue to 60 days, and strictly filter out stale items (> 60 days) outside the selected period.

    const MAX_ACTIVE_OVERDUE_DAYS = 60;



    const allItems: IOperationItem[] = useMemo(() => {

        if (!activities) return [];

        const items: IOperationItem[] = [];

        const today = moment().startOf('day');

        const selStart = moment(selectedDate.startDate).startOf('day');

        const selEnd = moment(selectedDate.endDate).endOf('day');



        const getRescheduleInfo = (track: any) => {

            if (track?.rescheduleInfo && track.rescheduleInfo.isRescheduled) {

                return track.rescheduleInfo;

            }

            if (track?.notes && track.notes.includes('[Reschedule:')) {

                const match = track.notes.match(/\[Reschedule:\s*([^\]]+)\]([\s\S]*)/);

                if (match) {

                    return {

                        isRescheduled: true,

                        reason: match[1].trim(),

                        notes: match[2]?.trim() || '',

                        fullNote: track.notes,

                    };

                }

            }

            return undefined;

        };

        // 1. Loading Trackings

        (activities.loading || []).forEach((loading) => {

            const orderNum = loading.order?.orderNumber || loading.orderNumber;

            const hasOrder = !!(loading.orderId || orderNum || loading.order);

            if (!hasOrder) return; // Only show items with published/active order



            const details = loading.loadDetails;

            const startDate = details?.actualStartDate || details?.estimatedStartDate;

            const endDate = details?.estimatedEndDate || details?.actualEndDate;



            // Guardrail: Hanya tampilkan tracking yang memiliki tanggal jadwal riil

            if (!startDate && !endDate) {

                return;

            }

            const dateStr = startDate || endDate || loading.createdAt || moment().toISOString();

            const itemDate = moment(dateStr);

            const rawStatus = (loading.status || 'scheduled').toLowerCase();

            // Guardrail: Exclude ghost auto-generated placeholder load trackings
            const hasOrderContainers = Boolean(
                ((loading.order as any)?.containers && (loading.order as any).containers.length > 0) ||
                ((loading as any)?.containers && (loading as any).containers.length > 0)
            );
            const hasScheduleDates = Boolean(startDate || endDate);
            const isGhostLoad = loading.type === 'load' && 
                (rawStatus === 'estimated' || !loading.status) &&
                !loading.container && 
                !hasOrderContainers &&
                !loading.orderTruck && 
                !hasScheduleDates &&
                !details?.actualStartDate;
            if (isGhostLoad) return;

            const isOrderDone = (loading.order?.status as any)?.uniqueKey === 'completed' || (loading.order?.status as any)?.key === 'completed' || Boolean(loading.order?.status?.name?.toLowerCase().includes('selesai'));

            const isDone = rawStatus === 'completed' || rawStatus === 'done' || rawStatus === 'selesai' ||
                Boolean(details?.actualEndDate && moment(details.actualEndDate).isValid()) ||
                isOrderDone;

            const isInProg = rawStatus === 'in_progress' || rawStatus === 'running' || rawStatus === 'in progress';

            const isToday = itemDate.isSame(today, 'day') || (endDate && moment(endDate).isValid() && moment(endDate).isSame(today, 'day'));

            const isFuture = itemDate.startOf('day').isAfter(today, 'day');

            const isPast = itemDate.startOf('day').isBefore(today, 'day');



            let normalizedStatus: 'completed' | 'in_progress' | 'overdue' | 'scheduled';

            let statusLabel = '';

            let isOverdue = false;

            let overdueDays = 0;



            if (isDone) {
                normalizedStatus = 'completed';
                statusLabel = 'Completed';
            } else if (isInProg) {
                // Sedang berjalan: hanya overdue jika ada target endDate dan endDate tersebut sudah lewat kemarin/sebelumnya
                const hasPastEnd = Boolean(endDate && moment(endDate).isValid() && moment(endDate).startOf('day').isBefore(today, 'day'));
                if (hasPastEnd) {
                    overdueDays = Math.max(1, today.diff(moment(endDate).startOf('day'), 'days'));
                    if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                        isOverdue = true;
                        normalizedStatus = 'overdue';
                        statusLabel = `Overdue (Loading in Progress ${overdueDays}d)`;
                    } else {
                        // Stale unclosed tracking (> 60 hari di masa lalu)
                        return;
                    }
                } else {
                    normalizedStatus = 'in_progress';
                    statusLabel = 'Loading in Progress';
                }
            } else if (isPast) {
                // Jadwal mulai sudah lewat tapi belum mulai berjalan
                overdueDays = Math.max(1, today.diff(itemDate.startOf('day'), 'days'));
                if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                    isOverdue = true;
                    normalizedStatus = 'overdue';
                    statusLabel = `Overdue Loading (${overdueDays}d)`;
                } else {
                    // Stale unclosed tracking (> 60 hari di masa lalu): di-exclude agar tidak membebani workspace
                    return;
                }
            } else {
                // Jadwal hari ini atau masa depan
                normalizedStatus = 'scheduled';
                statusLabel = itemDate.isSame(today, 'day') ? 'Scheduled Today' : 'Scheduled';
            }



            // Exclude items outside selected date range (kecuali item overdue aktif)

            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]') ||

                (endDate && moment(endDate).isValid() ? moment(endDate).isBetween(selStart, selEnd, undefined, '[]') : false);

            if (!isOverdue && !isWithinSelectedRange) {

                return;

            }



            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (loading.orderId ? `Order #${String(loading.orderId).substring(0, 8)}` : '');



            items.push({

                id: `load_${loading.id}`,

                rawId: loading.id,

                itemType: 'tracking',

                activityType: 'loading',

                activityLabel: 'Depot Loading',

                activityColor: '#f59e0b',

                activityIcon: faBoxesPacking,

                orderId: loading.orderId,

                orderNumber: orderLabel,
                rawOrderNumber: (loading as any)?.rawOrderNumber || (loading.order as any)?.orderNumber || (loading as any)?.orderNumber,

                customerName: (loading.order as any)?.customer?.name,
                salesName: (loading as any)?.salesName || (loading.order as any)?.salesName,
                sales: (loading as any)?.sales || (loading.order as any)?.sales || [],

                containers: loading.container ? [{

                    id: loading.container.id,

                    containerNumber: loading.container.containerNumber || 'No Container',

                    sealNumber: loading.container.sealNumber,

                    size: loading.container.size

                }] : (((loading.order as any)?.containers || []).map((c: any) => ({
                    id: c.id,
                    containerNumber: c.containerNumber || 'No Container',
                    sealNumber: c.sealNumber,
                    size: c.size
                }))),

                locationText: details?.address && details.address !== 'ededed' ? details.address : 'Depot / Loading Warehouse',

                scheduledDate: dateStr,

                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),

                estimatedArrival: details?.estimatedEndDate,

                actualDate: details?.actualStartDate,

                isOverdue,

                overdueDays,

                status: rawStatus,

                normalizedStatus,

                statusLabel,

                notes: loading.notes,

                rescheduleInfo: getRescheduleInfo(loading),

                detailsLink: `/orders/${loading.orderId}/trackings`,

                orderLink: `/orders/${loading.orderId}`,

                rawObject: loading,

            });

        });

        // 2. Unloading Trackings

        (activities.unloading || []).forEach((unloading) => {

            const orderNum = unloading.order?.orderNumber || unloading.orderNumber;

            const hasOrder = !!(unloading.orderId || orderNum || unloading.order);

            if (!hasOrder) return; // Only show items with published/active order



            const details = unloading.unloadingDetails;

            const startDate = details?.actualStartDate || details?.estimatedStartDate;

            const endDate = details?.estimatedEndDate || details?.actualEndDate;



            // Guardrail: Hanya tampilkan tracking yang memiliki tanggal jadwal riil

            if (!startDate && !endDate) {

                return;

            }

            const dateStr = startDate || endDate || unloading.createdAt || moment().toISOString();

            const itemDate = moment(dateStr);

            const rawStatus = (unloading.status || 'scheduled').toLowerCase();

            // Guardrail: Exclude ghost auto-generated placeholder unload trackings
            const hasOrderContainers = Boolean(
                ((unloading.order as any)?.containers && (unloading.order as any).containers.length > 0) ||
                ((unloading as any)?.containers && (unloading as any).containers.length > 0)
            );
            const hasScheduleDates = Boolean(startDate || endDate);
            const isGhostUnload = unloading.type === 'unload' && 
                (rawStatus === 'estimated' || !unloading.status) &&
                !unloading.container && 
                !hasOrderContainers &&
                !unloading.orderTruck && 
                !hasScheduleDates &&
                !details?.actualStartDate;
            if (isGhostUnload) return;

            const isOrderDone = (unloading.order?.status as any)?.uniqueKey === 'completed' || (unloading.order?.status as any)?.key === 'completed' || Boolean(unloading.order?.status?.name?.toLowerCase().includes('selesai'));

            const isDone = rawStatus === 'completed' || rawStatus === 'done' || rawStatus === 'selesai' ||
                Boolean(details?.actualEndDate && moment(details.actualEndDate).isValid()) ||
                isOrderDone;

            const isInProg = rawStatus === 'in_progress' || rawStatus === 'running' || rawStatus === 'in progress';

            const isToday = itemDate.isSame(today, 'day') || (endDate && moment(endDate).isValid() && moment(endDate).isSame(today, 'day'));

            const isFuture = itemDate.startOf('day').isAfter(today, 'day');

            const isPast = itemDate.startOf('day').isBefore(today, 'day');



            let normalizedStatus: 'completed' | 'in_progress' | 'overdue' | 'scheduled';

            let statusLabel = '';

            let isOverdue = false;

            let overdueDays = 0;



            if (isDone) {
                normalizedStatus = 'completed';
                statusLabel = 'Completed';
            } else if (isInProg) {
                // Sedang berjalan: hanya overdue jika ada target endDate dan endDate tersebut sudah lewat kemarin/sebelumnya
                const hasPastEnd = Boolean(endDate && moment(endDate).isValid() && moment(endDate).startOf('day').isBefore(today, 'day'));
                if (hasPastEnd) {
                    overdueDays = Math.max(1, today.diff(moment(endDate).startOf('day'), 'days'));
                    if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                        isOverdue = true;
                        normalizedStatus = 'overdue';
                        statusLabel = `Overdue (Unloading in Progress ${overdueDays}d)`;
                    } else {
                        // Stale unclosed tracking (> 60 hari di masa lalu)
                        return;
                    }
                } else {
                    normalizedStatus = 'in_progress';
                    statusLabel = 'Unloading in Progress';
                }
            } else if (isPast) {
                // Jadwal mulai sudah lewat tapi belum mulai berjalan
                overdueDays = Math.max(1, today.diff(itemDate.startOf('day'), 'days'));
                if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                    isOverdue = true;
                    normalizedStatus = 'overdue';
                    statusLabel = `Overdue Unloading (${overdueDays}d)`;
                } else {
                    // Stale unclosed tracking (> 60 hari di masa lalu): di-exclude agar tidak membebani workspace
                    return;
                }
            } else {
                // Jadwal hari ini atau masa depan
                normalizedStatus = 'scheduled';
                statusLabel = itemDate.isSame(today, 'day') ? 'Scheduled Today' : 'Scheduled';
            }



            // Exclude items outside selected date range (kecuali item overdue aktif)

            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]') ||

                (endDate && moment(endDate).isValid() ? moment(endDate).isBetween(selStart, selEnd, undefined, '[]') : false);

            if (!isOverdue && !isWithinSelectedRange) {

                return;

            }



            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (unloading.orderId ? `Order #${String(unloading.orderId).substring(0, 8)}` : '');



            items.push({

                id: `unload_${unloading.id}`,

                rawId: unloading.id,

                itemType: 'tracking',

                activityType: 'unloading',

                activityLabel: 'Unloading',

                activityColor: '#8b5cf6',

                activityIcon: faDolly,

                orderId: unloading.orderId,

                orderNumber: orderLabel,
                rawOrderNumber: (unloading as any)?.rawOrderNumber || (unloading.order as any)?.orderNumber || (unloading as any)?.orderNumber,

                customerName: (unloading.order as any)?.customer?.name,
                salesName: (unloading as any)?.salesName || (unloading.order as any)?.salesName,
                sales: (unloading as any)?.sales || (unloading.order as any)?.sales || [],

                containers: unloading.container ? [{

                    id: unloading.container.id,

                    containerNumber: unloading.container.containerNumber || 'No Container',

                    sealNumber: unloading.container.sealNumber,

                    size: unloading.container.size

                }] : (((unloading.order as any)?.containers || []).map((c: any) => ({
                    id: c.id,
                    containerNumber: c.containerNumber || 'No Container',
                    sealNumber: c.sealNumber,
                    size: c.size
                }))),

                locationText: details?.address && details.address !== 'ededed' ? details.address : 'Port / Unloading Warehouse',

                scheduledDate: dateStr,

                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),

                estimatedArrival: details?.estimatedEndDate,

                actualDate: details?.actualStartDate,

                isOverdue,

                overdueDays,

                status: rawStatus,

                normalizedStatus,

                statusLabel,

                notes: unloading.notes,

                rescheduleInfo: getRescheduleInfo(unloading),

                detailsLink: `/orders/${unloading.orderId}/trackings`,

                orderLink: `/orders/${unloading.orderId}`,

                rawObject: unloading,

            });

        });

        // 3. Dooring Trackings

        (activities.dooring || []).forEach((dooring) => {

            const orderNum = dooring.order?.orderNumber || dooring.orderNumber;

            const hasOrder = !!(dooring.orderId || orderNum || dooring.order);

            if (!hasOrder) return; // Only show items with published/active order



            const details = dooring.dooringDetails;

            const departureDate = details?.actualDepartureDate || details?.estimatedDepartureDate;

            const arrivalDate = details?.actualArrivalDate || details?.estimatedArrivalDate;



            // Guardrail: Hanya tampilkan tracking yang memiliki tanggal jadwal riil

            if (!departureDate && !arrivalDate) {

                return;

            }

            const dateStr = departureDate || dooring.createdAt || moment().toISOString();

            const itemDate = moment(dateStr);

            const rawStatus = (dooring.status || 'scheduled').toLowerCase();

            const isOrderDone = (dooring.order?.status as any)?.uniqueKey === 'completed' || (dooring.order?.status as any)?.key === 'completed' || Boolean(dooring.order?.status?.name?.toLowerCase().includes('selesai'));

            const isDone = rawStatus === 'completed' || rawStatus === 'arrived' || rawStatus === 'done' || rawStatus === 'selesai' ||
                Boolean(details?.actualArrivalDate && moment(details.actualArrivalDate).isValid()) ||
                isOrderDone;

            const isInProg = rawStatus === 'in_progress' || rawStatus === 'in_transit' || rawStatus === 'departed' || rawStatus === 'in progress';

            const isToday = itemDate.isSame(today, 'day') || (arrivalDate && moment(arrivalDate).isValid() && moment(arrivalDate).isSame(today, 'day'));

            const isFuture = itemDate.startOf('day').isAfter(today, 'day');

            const isPast = itemDate.startOf('day').isBefore(today, 'day');



            let normalizedStatus: 'completed' | 'in_progress' | 'overdue' | 'scheduled';

            let statusLabel = '';

            let isOverdue = false;

            let overdueDays = 0;



            if (isDone) {
                normalizedStatus = 'completed';
                statusLabel = 'Arrived at Destination';
            } else if (isInProg) {
                // Sedang dalam perjalanan: hanya overdue jika ada target arrivalDate dan arrivalDate tersebut sudah lewat kemarin/sebelumnya
                const hasPastArrival = Boolean(arrivalDate && moment(arrivalDate).isValid() && moment(arrivalDate).startOf('day').isBefore(today, 'day'));
                if (hasPastArrival) {
                    overdueDays = Math.max(1, today.diff(moment(arrivalDate).startOf('day'), 'days'));
                    if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                        isOverdue = true;
                        normalizedStatus = 'overdue';
                        statusLabel = `Overdue (In Transit ${overdueDays}d)`;
                    } else {
                        // Stale unclosed tracking (> 60 hari di masa lalu)
                        return;
                    }
                } else {
                    normalizedStatus = 'in_progress';
                    statusLabel = 'Dooring in Transit';
                }
            } else if (isPast) {
                // Jadwal dooring sudah lewat tapi belum mulai berjalan
                overdueDays = Math.max(1, today.diff(itemDate.startOf('day'), 'days'));
                if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                    isOverdue = true;
                    normalizedStatus = 'overdue';
                    statusLabel = `Overdue Dooring (${overdueDays}d)`;
                } else {
                    // Stale unclosed tracking (> 60 hari di masa lalu): di-exclude agar tidak membebani workspace
                    return;
                }
            } else {
                // Jadwal hari ini atau masa depan
                normalizedStatus = 'scheduled';
                statusLabel = itemDate.isSame(today, 'day') ? 'Scheduled Today' : 'Scheduled';
            }



            // Exclude items outside selected date range (kecuali item overdue aktif)

            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]') || 

                (arrivalDate ? moment(arrivalDate).isBetween(selStart, selEnd, undefined, '[]') : false);

            if (!isOverdue && !isWithinSelectedRange) {

                return;

            }



            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (dooring.orderId ? `Order #${String(dooring.orderId).substring(0, 8)}` : '');



            items.push({

                id: `door_${dooring.id}`,

                rawId: dooring.id,

                itemType: 'tracking',

                activityType: 'dooring',

                activityLabel: 'Dooring (Trucking)',

                activityColor: '#10b981',

                activityIcon: faTruck,

                truckRegNumber: details?.truck?.registrationNumber,

                vendorName: details?.truck?.vendor?.name,

                driverName: details?.driverName,

                driverPhone: details?.driverPhone,

                orderId: dooring.orderId,

                orderNumber: orderLabel,
                rawOrderNumber: (dooring as any)?.rawOrderNumber || (dooring.order as any)?.orderNumber || (dooring as any)?.orderNumber,

                customerName: (dooring.order as any)?.customer?.name,
                salesName: (dooring as any)?.salesName || (dooring.order as any)?.salesName,
                sales: (dooring as any)?.sales || (dooring.order as any)?.sales || [],

                containers: dooring.container ? [{

                    id: dooring.container.id,

                    containerNumber: dooring.container.containerNumber || 'No Container',

                    sealNumber: dooring.container.sealNumber,

                    size: dooring.container.size

                }] : (((dooring.order as any)?.containers || []).map((c: any) => ({
                    id: c.id,
                    containerNumber: c.containerNumber || 'No Container',
                    sealNumber: c.sealNumber,
                    size: c.size
                }))),

                originAddress: details?.originAddress,

                destinationAddress: details?.destinationAddress,

                locationText: details?.originAddress && details?.destinationAddress 

                    ? `${details.originAddress} ➔ ${details.destinationAddress}` 

                    : (details?.destinationAddress || 'Consignee Address'),

                scheduledDate: dateStr,

                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),

                estimatedArrival: details?.estimatedArrivalDate ? moment(details.estimatedArrivalDate).format('DD/MM/YYYY') : undefined,

                actualDate: details?.actualArrivalDate || details?.actualDepartureDate,

                isOverdue,

                overdueDays,

                status: rawStatus,

                normalizedStatus,

                statusLabel,

                notes: dooring.notes,

                rescheduleInfo: getRescheduleInfo(dooring),

                detailsLink: `/orders/${dooring.orderId}/trackings`,

                orderLink: `/orders/${dooring.orderId}`,

                rawObject: dooring,

            });

        });

        // 4. Ship Schedules

        (activities.shipSchedules || []).forEach((schedule) => {

            const orders = (schedule.orders || []).filter(o => o && (o.orderNumber || o.id));

            if (orders.length < 1) return; // Only show ship schedules that are attached/related to orders



            const departureDate = schedule.actualDeparture || schedule.estimatedDeparture;

            const arrivalDate = schedule.actualArrival || schedule.estimatedArrival;

            const dateStr = departureDate || schedule.createdAt || moment().toISOString();

            const itemDate = moment(dateStr);

            const rawStatus = (schedule.status || 'scheduled').toLowerCase();



            const isSailing = rawStatus === 'sailing' || rawStatus === 'in_progress';

            const isArrived = rawStatus === 'arrived' || rawStatus === 'completed' || rawStatus === 'docked' || rawStatus === 'sandar' ||
                Boolean(schedule.actualArrival && moment(schedule.actualArrival).isValid());

            const isDone = isArrived;

            const isToday = itemDate.isSame(today, 'day') || (arrivalDate && moment(arrivalDate).isValid() && moment(arrivalDate).isSame(today, 'day'));

            const isFuture = itemDate.startOf('day').isAfter(today, 'day');

            const isPast = itemDate.startOf('day').isBefore(today, 'day');



            let normalizedStatus: 'completed' | 'in_progress' | 'overdue' | 'scheduled';

            let statusLabel = '';

            let isOverdue = false;

            let overdueDays = 0;



            if (isDone) {
                normalizedStatus = 'completed';
                statusLabel = isArrived ? 'Docked' : 'Completed';
            } else if (isSailing) {
                // Kapal sedang berlayar: hanya overdue jika ada target arrivalDate (ETA) dan ETA tersebut sudah lewat kemarin/sebelumnya
                const hasPastArrival = Boolean(arrivalDate && moment(arrivalDate).isValid() && moment(arrivalDate).startOf('day').isBefore(today, 'day'));
                if (hasPastArrival) {
                    overdueDays = Math.max(1, today.diff(moment(arrivalDate).startOf('day'), 'days'));
                    if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                        isOverdue = true;
                        normalizedStatus = 'overdue';
                        statusLabel = `Overdue (Sailing ${overdueDays}d)`;
                    } else {
                        // Stale unclosed schedule (> 60 hari di masa lalu)
                        return;
                    }
                } else {
                    normalizedStatus = 'in_progress';
                    statusLabel = 'Vessel Sailing';
                }
            } else if (isPast) {
                // Jadwal keberangkatan kapal sudah lewat tapi belum berlayar
                overdueDays = Math.max(1, today.diff(itemDate.startOf('day'), 'days'));
                if (overdueDays <= MAX_ACTIVE_OVERDUE_DAYS) {
                    isOverdue = true;
                    normalizedStatus = 'overdue';
                    statusLabel = `Overdue Departure (${overdueDays}d)`;
                } else {
                    // Stale unclosed schedule (> 60 hari di masa lalu): di-exclude agar tidak membebani workspace
                    return;
                }
            } else {
                // Jadwal hari ini atau masa depan
                normalizedStatus = 'scheduled';
                statusLabel = itemDate.isSame(today, 'day') ? 'Departure Today' : 'Scheduled';
            }



            // Exclude items outside selected date range (kecuali item overdue aktif)

            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]') || 

                (arrivalDate ? moment(arrivalDate).isBetween(selStart, selEnd, undefined, '[]') : false);

            if (!isOverdue && !isWithinSelectedRange) {

                return;

            }



            const firstOrder = orders.length > 0 ? orders[0] : undefined;

            const orderLabel = firstOrder ? `#${OrderService.getOrderNumber(String(firstOrder.orderNumber))}${orders.length > 1 ? ` (+${orders.length - 1})` : ''}` : '';



            let shipColor = '#0284c7'; // Sky Blue for Scheduled Ship

            let shipLabel = 'Vessel Schedule';

            if (isSailing) {

                shipColor = '#0d9488'; // Teal Green for Kapal Berlayar

                shipLabel = 'Vessel Sailing';

            } else if (isArrived) {

                shipColor = '#059669'; // Emerald Green for Kapal Sandar

                shipLabel = 'Vessel Docked';

            }



            items.push({

                id: `ship_${schedule.id}`,

                rawId: schedule.id,

                itemType: 'ship',

                activityType: 'ship_departure',

                activityLabel: shipLabel,

                activityColor: shipColor,

                activityIcon: faShip,

                shipName: schedule.ship?.name || 'Shipping Vessel',

                voy: schedule.voy,

                vendorName: schedule.ship?.vendor?.name,

                orderId: firstOrder?.id,

                orderNumber: orderLabel || undefined,
                rawOrderNumber: firstOrder ? String(firstOrder.orderNumber) : undefined,

                salesName: (firstOrder as any)?.salesName,
                sales: (firstOrder as any)?.sales || [],
                orders: orders.map(o => ({
                    id: o.id,
                    orderNumber: String(o.orderNumber),
                    salesName: (o as any).salesName,
                    sales: (o as any).sales,
                })),

                originAddress: schedule.originAddress,

                destinationAddress: schedule.destinationAddress,

                locationText: schedule.originAddress && schedule.destinationAddress

                    ? `${schedule.originAddress} ➔ ${schedule.destinationAddress}`

                    : (schedule.originAddress || 'Origin Port'),

                scheduledDate: dateStr,

                scheduledDateFormatted: `${schedule.actualDeparture ? 'ATD' : 'ETD'}: ${itemDate.format('DD/MM/YYYY')}`,

                estimatedArrival: arrivalDate ? moment(arrivalDate).format('DD/MM/YYYY') : undefined,

                actualDate: schedule.actualDeparture || schedule.actualArrival,

                isOverdue,

                overdueDays,

                status: rawStatus,

                normalizedStatus,

                statusLabel,

                notes: (schedule as any)?.notes || undefined,

                rescheduleInfo: (schedule.delays && schedule.delays.length > 0) ? {

                    isRescheduled: true,

                    reason: (schedule.delays[0] as any)?.reason || 'Vessel Delay',

                    notes: (schedule.delays[0] as any)?.notes || undefined,

                    fullNote: 'Vessel Delay',

                } : undefined,

                detailsLink: `/operationals/ships/${schedule.ship?.id}/schedules/${schedule.id}`,

                orderLink: firstOrder ? `/orders/${firstOrder.id}` : undefined,

                rawObject: schedule,

            });

        });

        // Sort items: overdue first, then in_progress, then scheduled, then completed

        return items.sort((a, b) => {

            const statusPriority: Record<string, number> = {

                overdue: 1,

                in_progress: 2,

                scheduled: 3,

                completed: 4

            };

            const prioA = statusPriority[a.normalizedStatus] || 5;

            const prioB = statusPriority[b.normalizedStatus] || 5;

            if (prioA !== prioB) return prioA - prioB;

            return moment(a.scheduledDate).valueOf() - moment(b.scheduledDate).valueOf();

        });

    }, [activities, selectedDate]);



    // Base items filtered by Type & Search (used for KPI summary cards and as base for status filtering)

    const typeAndSearchFilteredItems = useMemo(() => {

        return allItems.filter((item) => {

            // Type Filter

            if (typeFilter === 'ship' && item.itemType !== 'ship') return false;

            if (typeFilter === 'loading' && item.activityType !== 'loading') return false;

            if (typeFilter === 'unloading' && item.activityType !== 'unloading') return false;

            if (typeFilter === 'dooring' && item.activityType !== 'dooring') return false;



            // Search Filter
            if (searchKeyword.trim() !== '') {
                const q = searchKeyword.toLowerCase().trim();
                const cleanQ = q.replace(/^#/, '').trim();

                const matchShip = item.shipName?.toLowerCase().includes(q) || (cleanQ !== '' && item.shipName?.toLowerCase().includes(cleanQ));
                const matchVoy = item.voy?.toLowerCase().includes(q) || (cleanQ !== '' && item.voy?.toLowerCase().includes(cleanQ));

                // Comprehensive Order Number Matching (Formatted, Raw, Clean, Suffix, ID, or Multi-order)
                const itemOrderNum = item.orderNumber?.toLowerCase() || '';
                const itemRawOrderNum = item.rawOrderNumber?.toLowerCase() || '';
                const itemOrderId = item.orderId?.toLowerCase() || '';

                const matchOrder = 
                    itemOrderNum.includes(q) || 
                    (cleanQ !== '' && itemOrderNum.includes(cleanQ)) ||
                    itemRawOrderNum.includes(q) || 
                    (cleanQ !== '' && itemRawOrderNum.includes(cleanQ)) ||
                    (cleanQ !== '' && itemOrderId.includes(cleanQ)) ||
                    item.orders?.some(o => {
                        const on = o.orderNumber?.toLowerCase() || '';
                        const formattedOn = OrderService.getOrderNumber(on).toLowerCase();
                        const ordId = o.id?.toLowerCase() || '';
                        return on.includes(q) || 
                            (cleanQ !== '' && on.includes(cleanQ)) || 
                            formattedOn.includes(q) || 
                            (cleanQ !== '' && formattedOn.includes(cleanQ)) ||
                            (cleanQ !== '' && ordId.includes(cleanQ));
                    });

                const matchCust = item.customerName?.toLowerCase().includes(q) || (cleanQ !== '' && item.customerName?.toLowerCase().includes(cleanQ));
                const matchLoc = item.locationText?.toLowerCase().includes(q) || (cleanQ !== '' && item.locationText?.toLowerCase().includes(cleanQ));
                const matchTruck = item.truckRegNumber?.toLowerCase().includes(q) || (cleanQ !== '' && item.truckRegNumber?.toLowerCase().includes(cleanQ));
                const matchDriver = item.driverName?.toLowerCase().includes(q) || (cleanQ !== '' && item.driverName?.toLowerCase().includes(cleanQ));
                const matchVendor = item.vendorName?.toLowerCase().includes(q) || (cleanQ !== '' && item.vendorName?.toLowerCase().includes(cleanQ));
                const matchContainer = item.containers?.some(c => 
                    c.containerNumber.toLowerCase().includes(q) || 
                    (cleanQ !== '' && c.containerNumber.toLowerCase().includes(cleanQ))
                );

                if (!matchShip && !matchVoy && !matchOrder && !matchCust && !matchLoc && !matchTruck && !matchDriver && !matchVendor && !matchContainer) {
                    return false;
                }
            }



            return true;

        });

    }, [allItems, typeFilter, searchKeyword]);



    // KPI Summary Counts (reacts dynamically to Type Filter & Search)

    
    // Distinct Active / Running Orders Metric & Breakdown
    const activeOrdersRecap: IActiveOrdersRecap = useMemo(() => {
        const activeOrderMap = new Map<string, IActiveOrderInfo>();
        const totalOrderIds = new Set<string>();

        const breakdownSets = {
            loading: new Set<string>(),
            sailing: new Set<string>(),
            unloading: new Set<string>(),
            dooring: new Set<string>(),
            train: new Set<string>(),
        };

        const processOrder = (
            orderId: string, 
            orderNumber: string, 
            customerName: string | undefined, 
            salesName: string | undefined,
            sales: { id: string; name: string }[] | undefined,
            item: IOperationItem
        ) => {
            if (!orderId && !orderNumber) return;
            const key = orderId || orderNumber;
            totalOrderIds.add(key);

            const isActive = item.normalizedStatus !== 'completed';

            if (isActive) {
                if (item.activityType === 'loading') {
                    breakdownSets.loading.add(key);
                } else if (item.activityType === 'ship_departure' || item.activityType === 'ship_arrival') {
                    breakdownSets.sailing.add(key);
                } else if (item.activityType === 'unloading') {
                    breakdownSets.unloading.add(key);
                } else if (item.activityType === 'dooring') {
                    breakdownSets.dooring.add(key);
                } else if (item.activityType === 'train') {
                    breakdownSets.train.add(key);
                }

                if (!activeOrderMap.has(key)) {
                    activeOrderMap.set(key, {
                        orderId: orderId || key,
                        orderNumber: orderNumber || key,
                        customerName: customerName || item.customerName,
                        salesName: salesName || item.salesName,
                        sales: sales || item.sales,
                        activities: [],
                        activeActivitiesCount: 0,
                        hasOverdue: false,
                    });
                }

                const ordInfo = activeOrderMap.get(key)!;
                if (!ordInfo.customerName && item.customerName) ordInfo.customerName = item.customerName;
                if (!ordInfo.salesName && (salesName || item.salesName)) ordInfo.salesName = salesName || item.salesName;

                ordInfo.activities.push({
                    id: item.id,
                    activityType: item.activityType,
                    activityLabel: item.activityLabel,
                    activityColor: item.activityColor,
                    statusLabel: item.statusLabel,
                    normalizedStatus: item.normalizedStatus,
                    scheduledDateFormatted: item.scheduledDateFormatted,
                    isOverdue: item.isOverdue,
                    overdueDays: item.overdueDays,
                });
                ordInfo.activeActivitiesCount++;
                if (item.isOverdue) ordInfo.hasOverdue = true;
            }
        };

        typeAndSearchFilteredItems.forEach((item) => {
            if (item.orders && item.orders.length > 0) {
                item.orders.forEach((ord) => {
                    processOrder(ord.id, ord.orderNumber, item.customerName, ord.salesName, ord.sales, item);
                });
            } else if (item.orderId || item.orderNumber) {
                processOrder(item.orderId || '', item.orderNumber || '', item.customerName, item.salesName, item.sales, item);
            }
        });

        const activeOrders = Array.from(activeOrderMap.values()).sort((a, b) => {
            if (a.hasOverdue !== b.hasOverdue) return a.hasOverdue ? -1 : 1;
            return b.activeActivitiesCount - a.activeActivitiesCount;
        });

        return {
            totalActiveOrders: activeOrders.length,
            totalOrdersInFilter: totalOrderIds.size,
            breakdown: {
                loading: breakdownSets.loading.size,
                sailing: breakdownSets.sailing.size,
                unloading: breakdownSets.unloading.size,
                dooring: breakdownSets.dooring.size,
                train: breakdownSets.train.size,
            },
            activeOrders,
        };
    }, [typeAndSearchFilteredItems]);

    const counts = useMemo(() => {

        let total = typeAndSearchFilteredItems.length;

        let overdue = typeAndSearchFilteredItems.filter(i => i.normalizedStatus === 'overdue').length;

        let scheduled = typeAndSearchFilteredItems.filter(i => i.normalizedStatus === 'scheduled').length;

        let inProgress = typeAndSearchFilteredItems.filter(i => i.normalizedStatus === 'in_progress').length;

        let completed = typeAndSearchFilteredItems.filter(i => i.normalizedStatus === 'completed').length;

        return { total, overdue, scheduled, inProgress, completed };

    }, [typeAndSearchFilteredItems]);



    // Filtered Items for Table/Board view based on active status filter

    const filteredItems = useMemo(() => {

        return typeAndSearchFilteredItems.filter((item) => {

            // Status Filter

            if (statusFilter === 'overdue' && item.normalizedStatus !== 'overdue') return false;

            if (statusFilter === 'scheduled' && item.normalizedStatus !== 'scheduled') return false;

            if (statusFilter === 'in_progress' && item.normalizedStatus !== 'in_progress') return false;

            if (statusFilter === 'completed' && item.normalizedStatus !== 'completed') return false;



            return true;

        });

    }, [typeAndSearchFilteredItems, statusFilter]);



    const handleDateChange = (startDate: string, endDate: string) => {

        setSelectedDate({ startDate, endDate });

    };



    return (

        <div className={styles.container}>

            {/* Header, KPI Bar & Filters */}

            <WorkspaceHeader

                counts={counts}
                activeOrdersRecap={activeOrdersRecap}
                onOpenOrdersList={() => setIsOrdersModalOpen(true)}

                selectedDate={selectedDate}

                onDateChange={handleDateChange}

                statusFilter={statusFilter}

                onStatusFilterChange={setStatusFilter}

                typeFilter={typeFilter}

                onTypeFilterChange={setTypeFilter}

                searchKeyword={searchKeyword}

                onSearchChange={setSearchKeyword}

                viewMode={viewMode}

                onViewModeChange={setViewMode}

                onRefresh={loadActivities}

            />



            {/* Main Content Area */}

            {!loaded ? (

                <LoadingComponent label="Loading operational activities..." labelPosition="right" />

            ) : (

                <>

                    {viewMode === 'table' ? (

                        <WorkspaceTableView

                            items={filteredItems}

                            onQuickStatus={(item) => setActiveStatusItem(item)}

                            onReschedule={(item) => setActiveRescheduleItem(item)}

                        />

                    ) : (

                        <WorkspaceBoardView

                            items={filteredItems}

                            onQuickStatus={(item) => setActiveStatusItem(item)}

                            onReschedule={(item) => setActiveRescheduleItem(item)}

                        />

                    )}

                </>

            )}



            {/* Unified Status Update Modals (consistent with Order Trackings) */}
            {activeStatusItem && activeStatusItem.itemType === 'tracking' && activeStatusItem.orderId && (
                <OrderTrackingStatusForm
                    orderId={activeStatusItem.orderId}
                    trackingId={activeStatusItem.rawId}
                    allTrackingIds={
                        (activeStatusItem.activityType === 'loading'
                            ? activities?.loading
                            : (activeStatusItem.activityType === 'unloading'
                                ? activities?.unloading
                                : activities?.dooring)
                        )?.filter(t => t.orderId === activeStatusItem.orderId).map(t => t.id)
                    }
                    onDismissed={(refresh) => {
                        setActiveStatusItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}

            {activeStatusItem && activeStatusItem.itemType === 'ship' && (
                <ShipScheduleStatusForm
                    scheduleId={activeStatusItem.rawId}
                    onDismissed={(refresh) => {
                        setActiveStatusItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}

            {activeStatusItem && activeStatusItem.itemType === 'train' && (
                <TrainScheduleStatusForm
                    scheduleId={activeStatusItem.rawId}
                    onDismissed={(refresh) => {
                        setActiveStatusItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}

            {activeStatusItem && activeStatusItem.itemType === 'tracking' && !activeStatusItem.orderId && (
                <QuickStatusModal
                    item={activeStatusItem}
                    isOpen={true}
                    onDismiss={(refresh) => {
                        setActiveStatusItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}



            {/* Active Running Orders Modal */}
            <ActiveOrdersModal
                isOpen={isOrdersModalOpen}
                onDismiss={() => setIsOrdersModalOpen(false)}
                recap={activeOrdersRecap}
                selectedDate={selectedDate}
            />

            {/* Reschedule Modal */}
            {activeRescheduleItem && (
                <RescheduleModal
                    item={activeRescheduleItem}
                    allTrackingIds={
                        activeRescheduleItem.orderId
                            ? (activeRescheduleItem.activityType === 'loading'
                                ? activities?.loading
                                : (activeRescheduleItem.activityType === 'unloading'
                                    ? activities?.unloading
                                    : activities?.dooring)
                            )?.filter(t => t.orderId === activeRescheduleItem.orderId).map(t => t.id)
                            : undefined
                    }
                    isOpen={true}
                    onDismiss={(refresh) => {
                        setActiveRescheduleItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}

        </div>

    );

};



export default OperationsWorkspace;

